/**
 * Avatar del canale fuori dalla veste v4 (badge v3, modale Trasferisci, area
 * rappresentante, gestione canali): solo stili inline, nessun foglio scoped.
 * Emoji o immagine senza bordo (2026-10-27); senza avatar rende la
 * sigla colorata se `fallbackSigla`, altrimenti nulla. Decorativo: accanto c'e
 * sempre il nome del canale.
 */
import React from 'react';
import { channelTone, type ChannelLike } from './v4/channelPalette';
import { resolveChannelAvatar, type ChannelAvatarLike } from './channelAvatar';

/** Sotto i 14 px emoji e immagini non si leggono (stesso minimo di ChannelSwatch). */
const AVATAR_MIN_PX = 14;

export interface ChannelAvatarMarkProps {
  channel?: (ChannelLike & { avatar?: ChannelAvatarLike | null }) | null;
  size?: number;
  fallbackSigla?: boolean;
  className?: string;
}

const ChannelAvatarMark: React.FC<ChannelAvatarMarkProps> = ({ channel, size: requested = 16, fallbackSigla = false, className }) => {
  const av = resolveChannelAvatar(channel?.avatar);
  if (!av && !fallbackSigla) return null;
  // Il minimo vale solo per emoji/immagine: la sigla di ripiego resta alla misura chiesta.
  const size = av ? Math.max(requested, AVATAR_MIN_PX) : requested;
  const tone = channelTone(channel);
  const radius = Math.max(3, Math.round(size * 0.3));
  const base: React.CSSProperties = {
    width: size, height: size, flex: 'none', borderRadius: radius,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', verticalAlign: 'middle',
  };
  if (!av) {
    return (
      <span
        className={className}
        aria-hidden="true"
        data-channel-avatar="sigla"
        style={{ ...base, background: tone.color, color: '#fff', font: `700 ${Math.max(7, Math.round(size * 0.32))}px 'JetBrains Mono', monospace`, letterSpacing: '.03em' }}
      >
        {tone.sigla}
      </span>
    );
  }
  // Immagine/emoji del canale senza bordo (2026-10-27, prima un anello col colore
  // del canale): fondo e angoli come prima.
  const box: React.CSSProperties = { ...base, background: 'var(--a2-card, #fff)' };
  return (
    <span className={className} aria-hidden="true" data-channel-avatar={av.kind} style={box}>
      {av.kind === 'emoji' ? (
        <span style={{ fontSize: Math.round(size * 0.68), lineHeight: 1 }}>{av.emoji}</span>
      ) : (
        <img src={av.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      )}
    </span>
  );
};

export default ChannelAvatarMark;
